import React, { useState, useEffect } from "react";
import { DailogBox } from "../../components/Elements/DailogBox/DailogBox";
import CustomButton from "../../components/Elements/Buttons/CustomButtons";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import {
  createGroups,
  setGroupFormField,
  resetGroupForm,
} from "../../redux/group/groupSlice";

const GroupAddNew = ({ onOpen, onClose, onSave }) => {
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState({
    groupName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    description: "",
  });

  const [countriesList, setCountriesList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [selectedStateCode, setSelectedStateCode] = useState("");

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const API_KEY = "0ae5a8283890022255b0058d88f4397ecde12b3253eae7c2af2ef157c23f108b";

  const formData = useSelector((state) => state.group.groupForm);
  const handleChange = (key, value) => {
    dispatch(setGroupFormField({ key, value }));
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await axios.get(
        "https://api.countrystatecity.in/v1/countries",
        {
          headers: { "X-CSCAPI-KEY": API_KEY },
        }
      );
      setCountriesList(res.data);
    } catch (err) {
      console.error("Country fetch failed", err);
      setCountriesList([{ name: "India", iso2: "IN" }]); // Fallback
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchStates = async (countryCode) => {
    try {
      setLoadingStates(true);
      const res = await axios.get(
        `https://api.countrystatecity.in/v1/countries/${countryCode}/states`,
        {
          headers: { "X-CSCAPI-KEY": API_KEY },
        }
      );
      setStatesList(res.data);
    } catch (err) {
      console.error("State fetch failed", err);
      setStatesList([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCities = async (countryCode, stateCode) => {
    try {
      setLoadingCities(true);
      const res = await axios.get(
        `https://api.countrystatecity.in/v1/countries/${countryCode}/states/${stateCode}/cities`,
        {
          headers: { "X-CSCAPI-KEY": API_KEY },
        }
      );
      setCitiesList(res.data);
    } catch (err) {
      console.error("City fetch failed", err);
      setCitiesList([]);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    if (onOpen) {
      fetchCountries();
    } else {
      setStatesList([]);
      setCitiesList([]);
      setSelectedCountryCode("");
      setSelectedStateCode("");
    }
  }, [onOpen]);

  //----------------FORM VALIDATION---------
  const validateForm = () => {
    let isValid = true;
    const errors = {
      groupName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      pincode: "",
      country: "",
      description: "",
    };

    if (!formData.groupName.trim()) {
      errors.groupName = "Group Name is required";
      isValid = false;
    }

    if (!formData.address1.trim()) {
      errors.address1 = "Address is required";
      isValid = false;
    }
    if (!formData.address2.trim()) {
      errors.address2 = "Address is required";
      isValid = false;
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
      isValid = false;
    }

    if (!formData.state.trim()) {
      errors.state = "State is required";
      isValid = false;
    }

    if (!formData.pincode.trim()) {
      errors.pincode = "Pincode is required";
      isValid = false;
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      errors.pincode = "Pincode must be 6 digits";
      isValid = false;
    }

    if (!formData.country.trim()) {
      errors.country = "Country is required";
      isValid = false;
    }

    setErrorMessage(errors);
    return isValid;
  };

  //---------------SAVE FORM DATA AND PAYLOAD CREATION-------------
  const handleSave = async () => {
    const isValid = validateForm();
    if (!isValid) {
      console.log("Validation failed");
      return;
    }

    const payload = {
      groupName: formData.groupName,
      description: formData.description,
      address_1: formData.address1,
      address_2: formData.address2,
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      country: formData.country,
    };

    try {
      await dispatch(createGroups({ payload })).unwrap();
      dispatch(resetGroupForm()); // clear form
      onSave();
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <DailogBox open={onOpen} onCancel={onClose} width="85%" maxWidth="700px">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center border-b ">
          <h2 className="text-lg font-semibold">+ Add New Group</h2>
        </div>

        {/* Form */}
        <div className=" space-y-4 mt-4">
          {/* Group Name */}
          <div>
            <label className="text-sm mt-3 text-gray-600">Group Name</label>
            <input
              type="text"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Group Name"
              className="w-full mt-2 border rounded-md p-3 outline-none"
            />
            {errorMessage.groupName && (
              <div className="text-red-500 text-sm">
                {errorMessage.groupName}
              </div>
            )}
          </div>

          {/* Address Row 1 & 2 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Address 1</label>
              <input
                type="text"
                value={formData.address1}
                onChange={(e) => handleChange("address1", e.target.value)}
                className="w-full mt-1 border-b outline-none p-1"
              />
              {errorMessage.address1 && (
                <div className="text-red-500 text-sm">
                  {errorMessage.address1}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm text-gray-600">Address 2</label>
              <input
                type="text"
                value={formData.address2}
                onChange={(e) => handleChange("address2", e.target.value)}
                className="w-full mt-1 border-b outline-none p-1"
              />
              {errorMessage.address2 && (
                <div className="text-red-500 text-sm">
                  {errorMessage.address2}
                </div>
              )}
            </div>
          </div>

          {/* Country & State */}
          <div className="grid grid-cols-2 gap-4">
            {/* Country */}
            <div>
              <label className="text-sm text-gray-600">Country</label>
              <select
                value={formData.country}
                onChange={(e) => {
                  const countryName = e.target.value;
                  const countryObj = countriesList.find((c) => c.name === countryName);
                  handleChange("country", countryName);
                  handleChange("state", "");
                  handleChange("city", "");
                  setStatesList([]);
                  setCitiesList([]);
                  setSelectedCountryCode(countryObj?.iso2 || "");
                  if (countryObj?.iso2) {
                    fetchStates(countryObj.iso2);
                  }
                }}
                className="w-full mt-1 border-b outline-none p-1 bg-transparent"
              >
                <option value="">Select Country</option>
                {countriesList.map((country) => (
                  <option key={country.id || country.iso2} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errorMessage.country && (
                <div className="text-red-500 text-sm">
                  {errorMessage.country}
                </div>
              )}
            </div>

            {/* State */}
            <div>
              <label className="text-sm text-gray-600">State</label>
              <select
                value={formData.state}
                onChange={(e) => {
                  const stateName = e.target.value;
                  const stateObj = statesList.find((s) => s.name === stateName);
                  handleChange("state", stateName);
                  handleChange("city", "");
                  setCitiesList([]);
                  setSelectedStateCode(stateObj?.iso2 || "");
                  if (selectedCountryCode && stateObj?.iso2) {
                    fetchCities(selectedCountryCode, stateObj.iso2);
                  }
                }}
                className="w-full mt-1 border-b outline-none p-1 bg-transparent"
              >
                <option value="">Select State</option>
                {statesList.map((state) => (
                  <option key={state.id || state.iso2} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errorMessage.state && (
                <div className="text-red-500 text-sm">{errorMessage.state}</div>
              )}
            </div>
          </div>

          {/* City & Pincode */}
          <div className="grid grid-cols-2 gap-4">
            {/* City */}
            <div>
              <label className="text-sm text-gray-600">City</label>
              <select
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="w-full mt-1 border-b outline-none p-1 bg-transparent"
              >
                <option value="">Select City</option>
                {citiesList.map((city) => (
                  <option key={city.id || city.name} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
              {errorMessage.city && (
                <div className="text-red-500 text-sm">{errorMessage.city}</div>
              )}
            </div>

            {/* Pincode */}
            <div>
              <label className="text-sm text-gray-600">Pincode</label>
              <input
                type="text"
                value={formData.pincode}
                onChange={(e) => handleChange("pincode", e.target.value)}
                className="w-full mt-1 border-b outline-none p-1"
              />
              {errorMessage.pincode && (
                <div className="text-red-500 text-sm">
                  {errorMessage.pincode}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-gray-600">Description</label>
            <textarea
              rows="2"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full mt-1 border rounded-md p-2 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-3 mt-6  pt-4">
          <CustomButton
            buttonText="Cancel"
            variant="cancel"
            onClick={onClose}
          />
          <CustomButton buttonText="Save" variant="ok" onClick={handleSave} />
        </div>
      </div>
    </DailogBox>
  );
};

export default GroupAddNew;
